import { useState } from 'react';
import { ForecastDisseminationStatus } from '@app/api/dissemination/types';
import { TimelineTooltip } from './TimelineTooltip';

interface ModelTimelineProps {
  modelName: string;
  status: ForecastDisseminationStatus[];
  timeDomain: { start: Date; end: Date };
}

export function ModelTimeline({ modelName, status, timeDomain }: ModelTimelineProps) {
  const [hoveredForecast, setHoveredForecast] = useState<{
    forecast: ForecastDisseminationStatus;
    position: { x: number; y: number };
  } | null>(null);

  // Helper to check if timestamp has timezone info (Z or offset like +00:00)
  const hasTimezoneInfo = (timeStr: string): boolean => /Z$|[+-]\d{2}:\d{2}$/.test(timeStr);

  const getStatusColor = (forecast: ForecastDisseminationStatus) => {
    if (forecast.is_missing) {
      return 'bg-gray-400 dark:bg-gray-600';
    }

    const inProgress = isInProgress(forecast);
    const now = new Date();
    const utcInitString = hasTimezoneInfo(forecast.init_time)
      ? forecast.init_time
      : `${forecast.init_time}Z`;
    const initTime = new Date(utcInitString);
    const expectedDisseminationTime = forecast.expected_dissemination_time;

    // For in-progress forecasts, check if we're past the expected dissemination time
    if (inProgress && expectedDisseminationTime) {
      // Parse expected time (format: "HH:MM:SS")
      const [hours, minutes, seconds] = expectedDisseminationTime.split(':').map(Number);
      let expectedDateTime = new Date(
        Date.UTC(
          initTime.getUTCFullYear(),
          initTime.getUTCMonth(),
          initTime.getUTCDate(),
          hours,
          minutes,
          seconds || 0,
          0,
        ),
      );

      // If the combined datetime is before init_time, add 1 day
      if (expectedDateTime < initTime) {
        expectedDateTime = new Date(expectedDateTime.getTime() + 24 * 60 * 60 * 1000);
      }

      // If current time is past expected dissemination time, apply delay coloring
      if (now > expectedDateTime) {
        const delay = forecast.delay ?? 0;
        if (delay < 0) {
          return 'bg-green-500 dark:bg-green-600';
        } else if (delay < 30) {
          return 'bg-yellow-500 dark:bg-yellow-600';
        } else {
          return 'bg-red-500 dark:bg-red-600';
        }
      }

      // Still in progress and not yet expected, keep green
      return 'bg-green-500 dark:bg-green-600';
    }

    // Not in progress - use normal delay coloring
    const delay = forecast.delay ?? 0;
    if (delay < 0) {
      return 'bg-green-500 dark:bg-green-600';
    } else if (delay < 30) {
      return 'bg-yellow-500 dark:bg-yellow-600';
    } else {
      return 'bg-red-500 dark:bg-red-600';
    }
  };

  const isInProgress = (forecast: ForecastDisseminationStatus) => {
    return forecast.max_prediction_timedelta < forecast.expected_max_prediction_timedelta;
  };

  const getPosition = (initTime: string) => {
    const time = new Date(initTime).getTime();
    const start = timeDomain.start.getTime();
    const end = timeDomain.end.getTime();
    const range = end - start;

    if (range === 0) return 50;

    return ((time - start) / range) * 100;
  };

  const handleMouseEnter = (
    forecast: ForecastDisseminationStatus,
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHoveredForecast({
      forecast,
      position: {
        x: rect.left + rect.width / 2,
        y: rect.top,
      },
    });
  };

  const handleMouseLeave = () => {
    setHoveredForecast(null);
  };

  return (
    <div className="flex items-center border-b border-gray-200 dark:border-gray-700 py-3">
      <div className="w-40 flex-shrink-0 pr-4 font-medium text-gray-900 dark:text-gray-100">
        {modelName}
      </div>
      <div className="flex-1 relative h-8 bg-gray-50 dark:bg-gray-900 rounded">
        {status.map((forecast, index) => {
          const position = getPosition(forecast.init_time);
          const color = getStatusColor(forecast);
          const inProgress = isInProgress(forecast);

          return (
            <div
              key={`${forecast.init_time}-${index}`}
              className={`absolute top-0 bottom-0 w-1 cursor-pointer transition-all hover:w-2 ${color} ${
                inProgress ? 'animate-pulse' : ''
              }`}
              style={{
                left: `${position}%`,
                transform: 'translateX(-50%)',
              }}
              onMouseEnter={(e) => handleMouseEnter(forecast, e)}
              onMouseLeave={handleMouseLeave}
            />
          );
        })}
      </div>
      {hoveredForecast && <TimelineTooltip {...hoveredForecast} />}
    </div>
  );
}
